import { Vec2 } from './math';
import { DEFAULT_FLATTEN, entityPaths, type FlatPath, type FlattenOptions } from './flatten';
import { distancePointSegment } from './query';
import type { Entity } from './types';

/**
 * Merging: welding a pile of pieces into as few outlines as possible.
 *
 * Two shapes that share an edge, or a chain of lines that touches end to end, are separate
 * entities in the model â€” nothing else can then treat them as one object (hatch, offset, a
 * dimension). This module welds them: `join` takes whatever touches and makes it one run,
 * including open chains of lines and arcs. Cutting shapes against each other is *not* a merge;
 * that is the boolean operator in ./boolean.
 *
 * Everything is flattened first (curves become polylines within `chordTolerance`), because the
 * result has to be expressible as one entity; 0.05 mm of chord error is well under drawing scale.
 */

/** A welded run of geometry: its points, and whether it closes back on itself. */
export interface MergedRun {
  points: Vec2[];
  closed: boolean;
}

export interface MergeOptions {
  /** Endpoints closer than this are the same point (mm). */
  tolerance?: number;
  chordTolerance?: number;
  maxArcSegments?: number;
}

const DEFAULT_TOLERANCE = 0.05;

export function mergeEntities(
  entities: readonly Entity[],
  options: MergeOptions = {},
): MergedRun[] {
  const tolerance = Math.max(options.tolerance ?? DEFAULT_TOLERANCE, 1e-6);
  const flatten: FlattenOptions = {
    chordTolerance: options.chordTolerance ?? DEFAULT_FLATTEN.chordTolerance,
    maxArcSegments: options.maxArcSegments ?? DEFAULT_FLATTEN.maxArcSegments,
  };

  // The same object can reach a merge more than once (a chain wired in, and the chain's head too);
  // counting it twice would make its edges look "shared" and delete them.
  const unique: Entity[] = [];
  const seen = new Set<string>();
  for (const entity of entities) {
    if (seen.has(entity.id)) continue;
    seen.add(entity.id);
    unique.push(entity);
  }

  const paths = unique
    .flatMap((entity) => entityPaths(entity, flatten))
    .filter((path) => path.points.length >= 2);
  if (paths.length === 0) return [];

  const runs = segmentsOf(paths);
  return weldRuns(runs, tolerance);
}

// ------------------------------------------------------------------ segments

/** One flattened edge of the input, from `p` to `q`. */
export type Segment = readonly [Vec2, Vec2];

function segmentsOf(paths: readonly FlatPath[]): Segment[] {
  const out: Segment[] = [];
  for (const path of paths) {
    const points = path.points;
    for (let i = 1; i < points.length; i++) out.push([points[i - 1]!, points[i]!]);
    if (path.closed && points.length > 2) out.push([points[points.length - 1]!, points[0]!]);
  }
  return out;
}

/** Drops repeated vertices but keeps a closing point, so the run's ends still say it is closed. */
function dedupeAdjacent(points: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && last.distanceTo(p) < 1e-9) continue;
    out.push(p);
  }
  return out;
}

// --------------------------------------------------------------------- weld

/**
 * Chain segments whose endpoints meet. Endpoints are bucketed on a grid the size of the
 * tolerance, so welding a 200-arc circle stays linear instead of comparing every pair.
 */
export function weldRuns(
  segments: readonly Segment[],
  tolerance: number,
  keepSlicePoints = false,
): MergedRun[] {
  if (segments.length === 0) return [];
  const tol = Math.max(tolerance, 1e-9);
  const grid = tol;
  const runs = segments.map((segment) => ({
    points: [segment[0], segment[1]] as Vec2[],
    alive: true,
  }));
  const buckets = new Map<string, Array<{ run: number; end: 0 | 1 }>>();

  const keyOf = (p: Vec2): string => `${Math.round(p.x / grid)}:${Math.round(p.y / grid)}`;
  const index = (runIndex: number, end: 0 | 1): void => {
    const run = runs[runIndex]!;
    const point = end === 0 ? run.points[0]! : run.points[run.points.length - 1]!;
    const key = keyOf(point);
    const list = buckets.get(key);
    if (list) list.push({ run: runIndex, end });
    else buckets.set(key, [{ run: runIndex, end }]);
  };

  for (let i = 0; i < runs.length; i++) {
    index(i, 0);
    index(i, 1);
  }

  const partnerAt = (point: Vec2, skip: number): { run: number; end: 0 | 1 } | null => {
    const cx = Math.round(point.x / grid);
    const cy = Math.round(point.y / grid);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const list = buckets.get(`${cx + dx}:${cy + dy}`);
        if (!list) continue;
        for (const entry of list) {
          if (entry.run === skip) continue;
          const run = runs[entry.run]!;
          if (!run.alive) continue;
          const candidate = entry.end === 0 ? run.points[0]! : run.points[run.points.length - 1]!;
          if (candidate.distanceTo(point) <= tol) return entry;
        }
      }
    }
    return null;
  };

  const out: MergedRun[] = [];
  for (let start = 0; start < runs.length; start++) {
    const run = runs[start]!;
    if (!run.alive) continue;
    run.alive = false;
    let grew = true;
    while (grew) {
      grew = false;
      const tail = run.points[run.points.length - 1]!;
      const next = partnerAt(tail, start);
      if (next) {
        const other = runs[next.run]!;
        other.alive = false;
        const points = next.end === 1 ? [...other.points].reverse() : other.points;
        const start0 = points[0]!;
        run.points.push(...(start0.distanceTo(tail) <= tol ? points.slice(1) : points));
        grew = true;
        continue;
      }
      const head = run.points[0]!;
      const previous = partnerAt(head, start);
      if (previous) {
        const other = runs[previous.run]!;
        other.alive = false;
        const points = previous.end === 0 ? [...other.points].reverse() : other.points;
        const end = points[points.length - 1]!;
        run.points.unshift(...(end.distanceTo(head) <= tol ? points.slice(0, -1) : points));
        grew = true;
      }
    }

    const points = dedupeAdjacent(run.points);
      const closes = points.length > 2 && points[0]!.distanceTo(points[points.length - 1]!) <= tol;
      const outline = keepSlicePoints
        ? closes
          ? points.slice(0, -1)
          : points
        : simplify(closes ? points.slice(0, -1) : points, closes);
      out.push({ points: outline, closed: closes });
  }
  return out;
}

/**
 * Drops a vertex that only sits on the straight line between its neighbours, so welding two
 * rectangles along a shared edge reads as one rectangle instead of a six-point outline.
 */
function simplify(points: readonly Vec2[], closed: boolean, tolerance = 1e-6): Vec2[] {
  const out = [...points];
  if (out.length <= 3) return out;
  let changed = true;
  while (changed) {
      changed = false;
      for (let i = 0; i < out.length; i++) {
        if (!closed && (i === 0 || i === out.length - 1)) continue;
        if (out.length <= 3) break;
        const previous = out[(i - 1 + out.length) % out.length]!;
        const current = out[i]!;
        const next = out[(i + 1) % out.length]!;
        const along = current.sub(previous).dot(next.sub(current));
        if (along < 0) continue;
        if (distancePointSegment(previous, next, current) > tolerance) continue;
        out.splice(i, 1);
        changed = true;
        break;
      }
  }
  return out;
}



